//src/design-system/Molecules/ForgetPasswordModal/index.tsx
import React, { useState } from "react";
import { Modal } from "@/design-system/Atoms/Modal";
import { Typography, Button } from "@/design-system/Atoms";
import { InputField } from "@/design-system/Atoms/InputField";
import {
  forgotPasswordSchema,
  type ForgotPasswordSchema,
} from "@/validation-schemas/forgotPassword-form-schemas";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForgetPasswordService } from "@/rtkQuery/features/hooks/useForgetPasswordService";
import { useInViewOnce } from "@/hooks/useInViewOnce";

interface ForgetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ForgetPasswordModal: React.FC<ForgetPasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ForgotPasswordSchema>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const { forgotPassword, forgotPasswordLoading } = useForgetPasswordService();
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [responseFlag, setResponseFlag] = useState<boolean>(false);
  const { ref } = useInViewOnce<HTMLDivElement>();

  const onSubmit = async (data: ForgotPasswordSchema) => {
    if (!data.email) return;
    const result = await forgotPassword(data.email);
    if (result) {
      setResponseMessage(result.message);
      setResponseFlag(result?.result);
      if (result?.result) {
        setTimeout(() => {
          reset();
          onClose();
          setResponseMessage(null);
        }, 5000);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset();  
        setResponseMessage(null);  
        onClose();  
      }}
      title="Forgot Password"
      size="xl"
      className="max-h-[92vh] mx-auto max-w-[42rem]"
    >
      <div className="p-6">
        <div className="prose prose-sm max-w-none">
          <Typography
            variant="sectionDescriptionSmall"
            as="p"
            className="mb-4 text-center"
          >
            Provide your registered email to receive a password reset link.
          </Typography>
          <div className="bg-[#f6f7f9] p-6 lg:w-full mx-auto rounded-[20px]">
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="mb-6">
                <InputField
                  placeholder="Email"
                  type="email"
                  register={register("email")}
                  error={errors.email?.message}
                  className="md:col-span-2"
                />
              </div>

              {responseMessage && (
                <div
                  ref={ref}
                  className={`animate-fade-in-up mt-6 mb-6 p-4 rounded-md text-sm font-bold border ${
                    responseMessage && responseFlag
                      ? "border-green-300 bg-green-50 text-green-700"
                      : "border-red-300 bg-red-50 text-red-700"
                  }`}
                  dangerouslySetInnerHTML={{ __html: responseMessage }}
                />
              )}
              <Button
                variant="secondary"
                size="xl"
                className="!w-full"
                type="submit"
                disabled={forgotPasswordLoading}
                loading={forgotPasswordLoading}
              >
                {forgotPasswordLoading ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ForgetPasswordModal;
